import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/tailwind.utils'
import { Folder } from '../collections.types'

type FolderFormDialogProps = {
  // Only used to rename/edit an existing collection's details; null while the dialog is closed.
  folder: Folder | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: { name: string; description: string | null }) => void
  isSubmitting?: boolean
  // Set by the caller on a duplicate-name conflict; cleared locally once the user edits the name.
  nameError?: string | null
}

const EMPTY_VALUES = { name: '', description: '' }

export const FolderFormDialog = ({
  folder,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  nameError,
}: FolderFormDialogProps) => {
  const [values, setValues] = useState(EMPTY_VALUES)
  const [dirtyName, setDirtyName] = useState(false)

  useEffect(() => {
    if (!open || !folder) return
    setValues({ name: folder.name, description: folder.description ?? '' })
    setDirtyName(false)
  }, [open, folder])

  // A fresh nameError applies to the name currently in the field, so it overrides any earlier dirty flag.
  useEffect(() => {
    if (nameError) setDirtyName(false)
  }, [nameError])

  const isValid = values.name.trim().length > 0
  const visibleNameError = dirtyName ? null : nameError

  const handleSubmit = () => {
    if (!isValid) return
    onSubmit({ name: values.name.trim(), description: values.description.trim() || null })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename collection</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="folder-name">Name</Label>
            <Input
              id="folder-name"
              value={values.name}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, name: e.target.value }))
                setDirtyName(true)
              }}
              aria-invalid={!!visibleNameError}
              className={cn(
                'border border-border',
                visibleNameError && 'border-destructive focus-visible:ring-destructive',
              )}
              autoFocus
            />
            {visibleNameError && <p className="text-sm text-destructive">{visibleNameError}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="folder-description">Description (optional)</Label>
            <Textarea
              id="folder-description"
              value={values.description}
              onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="border border-border"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
